import React, { useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router';
import { MobileMargin } from 'ui/views/layouts/BaseLayout';
import PageTitle from 'ui/views/layouts/BaseLayout/PageTitle';
import Card from 'ui/views/cards/Card';
import { CardGrid } from 'ui/views/cards/CardGrid';
import SectionHeading from 'ui/elements/SectionHeading';
import Chip from 'ui/elements/Chip';
import Button from 'ui/elements/buttons/Button';
import ButtonList from 'ui/elements/buttons/ButtonList';
import EditAction from 'ui/elements/actions/EditAction';
import PopMenu from 'ui/modules/PopMenu';
import ShareIcon from 'ui/elements/icons/ShareIcon';
import SendIcon from 'ui/elements/icons/SendIcon';
import CopyIcon from 'ui/elements/icons/CopyIcon';
import InfoIcon from 'ui/elements/icons/InfoIcon';
import CheckMarkCircleIcon from 'ui/elements/icons/CheckmarkCircleIcon';
import CompanyCard from 'domain/companies/CompanyCard/CompanyCard';
import UserConversationDialog from 'domain/conversations/UserConversationDialog';
import ToggleViewMode from 'pages/Community/CommunityMembers/Companies/ToggleViewMode';
import useViewMode from 'pages/Community/CommunityMembers/Companies/useViewMode';
import { ShortlistDeleteDialog, ShortlistSettingsDialog } from 'pages/Shortlists/ShortlistDialog';
import { ShortlistDescription, useUpdateShortlist } from 'pages/Shortlists/utils';
import { useShortlistedCompanies, useShortlistSuggestions } from 'apis/CompanyAPI/users/useShortlistedCompanies';
import { usersApi } from 'apis/CompanyAPI/users/usersApi';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import useNotify from 'hooks/useNotify';
import useRoute from 'hooks/useRoute';
import Resources from 'util/resource/Resources';
import Resource from 'util/resource/Resource';
import { getOrElse, isValidating } from 'util/resource';
import useLazyResource from 'util/resource/useLazyResource';
import { timeSince } from 'util/dateUtils';
import { scrollToTop } from 'util/scroll';
import { companyUrls, userUrls } from 'urls';
import { Shortlist } from 'types/user';
import CollectionTabs from './CollectionTabs';
import CollectionCompaniesTable from './CollectionCompaniesTable';
import CompaniesFilters, { CompaniesFilterValues } from './CompaniesFilters';
import SuggestionCard from './SuggestionCard';
import useCollectionShortlists, { invalidateCollectionCompanies } from './useCollectionShortlists';
import { latestActivity } from './latestActivity';

// "3 days ago" and "yesterday" read well after "updated"; dates keep their capitals
const relativeTime = (date: string) => {
  const text = timeSince(date);
  return text.endsWith(' ago') || text === 'Yesterday' || text === 'Just now' ? text.toLowerCase() : text;
};

/** One of the user's own lists, behind the `collections-hub` feature preview. */
export default function CollectionPage() {
  const match = useRouteMatch<{ id: string }>();
  const { resource: shortlistResource, setShortlists } = useCollectionShortlists();

  return (
    <Resources resources={[shortlistResource]}>
      {([shortlists]) => {
        const shortlist = shortlists.values.find(list => list.id === match.params.id);
        return shortlist ? (
          <CollectionContent shortlist={shortlist} shortlists={shortlists.values} setShortlists={setShortlists} />
        ) : null;
      }}
    </Resources>
  );
}

function CollectionContent({
  shortlist,
  shortlists,
  setShortlists,
}: {
  shortlist: Shortlist;
  shortlists: Shortlist[];
  setShortlists: ReturnType<typeof useCollectionShortlists>['setShortlists'];
}) {
  const { push } = useRoute();
  const notify = useNotify();
  const { viewMode, setViewMode } = useViewMode();
  const [filters, setFilters] = useState<CompaniesFilterValues>({ orderBy: 'latest-activity', raising: false });
  const { resource: companiesResource } = useShortlistedCompanies({ shortlistId: shortlist.id, ...filters });
  const { resource: suggestionsResource } = useShortlistSuggestions(shortlist.id);
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [isShareInChatOpen, setShareInChatOpen] = useState(false);
  const [addingCompanyId, setAddingCompanyId] = useState<number>();

  const { copy, showCopiedSuccess } = useCopyToClipboard(
    `${location.origin}${userUrls.collections.view(shortlist.id)}`,
  );

  useEffect(() => {
    scrollToTop();
  }, [shortlist.id]);

  const [updateList, isUpdatingList] = useUpdateShortlist({
    onSuccess: () => {
      notify('success', 'List updated');
      setShortlists();
      setSettingsOpen(false);
    },
    onFailure: error => notify('error', error ?? 'Could not update the list'),
  });

  const [deleteList, isDeletingList] = useLazyResource((listId: UUID) => usersApi.shortlists.delete(listId), {
    onSuccess: () => {
      setShortlists();
      push(userUrls.collections.list);
      notify('success', 'List deleted');
    },
    onFailure: message => notify('error', message || 'Could not delete the list'),
  });

  const [addCompany] = useLazyResource(
    (companyId: number) =>
      usersApi.shortlists.updateCompanies(shortlist.id, [
        ...shortlist.companies.map(company => company.id.toString()),
        companyId.toString(),
      ]),
    {
      onSuccess: () => {
        setAddingCompanyId(undefined);
        setShortlists();
        invalidateCollectionCompanies();
      },
      onFailure: () => {
        setAddingCompanyId(undefined);
        notify('error', 'Could not add the company to the list');
      },
    },
  );

  const companies = getOrElse(companiesResource, { values: [] }).values;
  const raisingCount = companies.filter(it => it.card.hasActiveFundingRound).length;
  const lastActivity = companies
    .map(it => latestActivity(it.card)?.at)
    .filter((at): at is string => !!at)
    .sort()
    .pop();
  const stats = [
    `${shortlist.companies.length} ${shortlist.companies.length === 1 ? 'company' : 'companies'}`,
    !filters.raising && raisingCount > 0 ? `${raisingCount} raising now` : undefined,
    lastActivity ? `updated ${relativeTime(lastActivity)}` : undefined,
  ].filter(Boolean);

  const shareItems = shortlist.isPublic
    ? [
        { icon: <SendIcon />, text: <span>Share in chat</span>, onClick: () => setShareInChatOpen(true) },
        {
          icon: showCopiedSuccess ? <CheckMarkCircleIcon color="green" /> : <CopyIcon />,
          text: <span>Copy link</span>,
          onClick: () => {
            copy();
            notify('success', 'Collection link copied to clipboard');
          },
        },
      ]
    : [
        {
          icon: <InfoIcon />,
          text: <span>List must be public in order to share. Click to make public.</span>,
          onClick: () => updateList({ id: shortlist.id, payload: { isPublic: true } }),
        },
      ];

  return (
    <>
      <PageTitle title={shortlist.name} />
      <MobileMargin>
        <CollectionTabs shortlists={shortlists} activeShortlistId={shortlist.id} setShortlists={setShortlists} />
        <Card className="u-section-spacing-bottom">
          <div className="u-flex u-flex-space-between u-flex-wrap u-flex--gap-1">
            <div style={{ maxWidth: '40rem' }}>
              <div className="text-h3 u-flex-align-center u-flex--gap-half u-half-spacing-bottom">
                {shortlist.name}
                {shortlist.isPublic && <Chip color="blue-light" label="Public" textTransform="uppercase" />}
                <button onClick={() => setSettingsOpen(true)}>
                  <EditAction tooltip="Edit list settings" />
                </button>
              </div>
              <ShortlistDescription description={shortlist.description} />
              <div className="text-metadata u-half-spacing-top">{stats.join(' · ')}</div>
            </div>
            <ButtonList>
              <PopMenu
                items={shareItems}
                renderAnchor={onClick => (
                  <Button onClick={onClick} kind="tertiary" color="grey">
                    <ShareIcon className="u-quarter-spacing-right" />
                    Share
                  </Button>
                )}
              />
            </ButtonList>
          </div>
        </Card>
        <SectionHeading heading="Companies">
          <div className="u-flex u-flex-align-center u-flex--gap-half">
            <CompaniesFilters values={filters} onChange={setFilters} />
            <ToggleViewMode size="compact" viewMode={viewMode} setViewMode={setViewMode} />
          </div>
        </SectionHeading>
        {viewMode === 'list' ? (
          <CollectionCompaniesTable
            companies={companies}
            shortlists={shortlists}
            setShortlists={setShortlists}
            isLoading={isValidating(companiesResource)}
            emptyMessage={
              filters.raising ? 'None of the companies in this list are raising right now.' : 'This list is empty.'
            }
          />
        ) : (
          <Resource resource={companiesResource}>
            {({ values }) =>
              values.length > 0 ? (
                <CardGrid
                  mobile={{ columnCount: 1 }}
                  tablet={{ columnCount: 2 }}
                  desktop={{ columnCount: 2 }}
                  xl={{ columnCount: 3 }}
                  xxl={{ columnCount: 4 }}
                >
                  {values.map(({ card }) => (
                    <CompanyCard
                      key={card.details.id}
                      company={card}
                      action={{ type: 'href', handler: company => companyUrls.overview(company.slug, 'profile') }}
                      shortlists={shortlists}
                      setShortlists={setShortlists}
                    />
                  ))}
                </CardGrid>
              ) : (
                <p className="text-body">
                  {filters.raising
                    ? 'None of the companies in this list are raising right now.'
                    : 'This list is empty.'}
                </p>
              )
            }
          </Resource>
        )}
        <Resource resource={suggestionsResource} renderLoading="Nothing" renderError="Nothing">
          {suggestions =>
            suggestions.length > 0 && (
              <div className="u-section-spacing-top">
                <SectionHeading heading={`More for ${shortlist.name}`} />
                <CardGrid
                  mobile={{ columnCount: 1 }}
                  tablet={{ columnCount: 2 }}
                  desktop={{ columnCount: 3 }}
                  xl={{ columnCount: 3 }}
                  xxl={{ columnCount: 3 }}
                >
                  {suggestions.map(company => (
                    <SuggestionCard
                      key={company.details.id}
                      company={company}
                      isAdding={addingCompanyId === company.details.id}
                      onAdd={() => {
                        setAddingCompanyId(company.details.id);
                        addCompany(company.details.id);
                      }}
                    />
                  ))}
                </CardGrid>
              </div>
            )
          }
        </Resource>
      </MobileMargin>
      {isShareInChatOpen && (
        <UserConversationDialog
          conversation={{ type: 'new' }}
          initialMessage={`${location.origin}${userUrls.collections.view(shortlist.id)}`}
          closeConversation={() => setShareInChatOpen(false)}
        />
      )}
      {isSettingsOpen && (
        <ShortlistSettingsDialog
          mode="edit"
          shortlist={shortlist}
          onClose={() => setSettingsOpen(false)}
          onSubmit={list => updateList({ id: list.id, payload: list })}
          onDelete={() => setDeleteOpen(true)}
          isSaving={isUpdatingList}
        />
      )}
      {isDeleteOpen && (
        <ShortlistDeleteDialog
          dialogHandler={{ isOpen: isDeleteOpen, open: () => setDeleteOpen(true), close: () => setDeleteOpen(false) }}
          content={
            <>
              Are you sure you would like to delete the list <strong>{shortlist.name}</strong>?
            </>
          }
          onConfirm={() => deleteList(shortlist.id)}
          title="Confirm delete"
          isSaving={isDeletingList}
        />
      )}
    </>
  );
}
